/**
 * OpenTelemetry browser SDK bootstrap.
 *
 * Additive sink alongside the existing custom telemetry pipeline
 * (backendAdapter + GA4). When VITE_OTEL_ENDPOINT is unset or not a
 * parseable absolute URL, this module is a complete no-op — matching the
 * backend's safe-rollout shape so missing config never breaks the app.
 */

import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';

let initialized = false;
let otelLogger: ReturnType<typeof logs.getLogger> | null = null;

const DEFAULT_SERVICE_NAME = 'dorat-customer-web';

function isValidAbsoluteHttpUrl(value: string | undefined): value is string {
    if (!value) return false;
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

export function initOtel(): void {
    if (initialized) return;
    if (typeof window === 'undefined') return;

    const endpoint = import.meta.env.VITE_OTEL_ENDPOINT;
    if (!isValidAbsoluteHttpUrl(endpoint)) {
        return;
    }

    const authToken = import.meta.env.VITE_OTEL_AUTH_TOKEN;
    const headers: Record<string, string> = {};
    if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
    }

    const base = endpoint.replace(/\/+$/, '');
    const serviceName = import.meta.env.VITE_OTEL_SERVICE_NAME || DEFAULT_SERVICE_NAME;

    // Prefer an explicit VITE_OTEL_DEPLOYMENT_ENV (set per env in the
    // deploy workflows: "staging" / "production"). Fall back to Vite's
    // MODE for local dev (which gives "development").
    const deploymentEnv = import.meta.env.VITE_OTEL_DEPLOYMENT_ENV || import.meta.env.MODE;

    const resource = resourceFromAttributes({
        'service.name': serviceName,
        'deployment.environment': deploymentEnv,
        'app.version': __APP_VERSION__,
    });

    const tracerProvider = new WebTracerProvider({
        resource,
        spanProcessors: [
            new BatchSpanProcessor(
                new OTLPTraceExporter({
                    url: `${base}/v1/traces`,
                    headers,
                }),
            ),
        ],
    });
    tracerProvider.register();

    registerInstrumentations({
        instrumentations: [
            new DocumentLoadInstrumentation(),
            new FetchInstrumentation({
                propagateTraceHeaderCorsUrls: [/.*/],
            }),
            new XMLHttpRequestInstrumentation({
                propagateTraceHeaderCorsUrls: [/.*/],
            }),
        ],
    });

    const loggerProvider = new LoggerProvider({
        resource,
        processors: [
            new BatchLogRecordProcessor(
                new OTLPLogExporter({
                    url: `${base}/v1/logs`,
                    headers,
                }),
            ),
        ],
    });
    logs.setGlobalLoggerProvider(loggerProvider);
    otelLogger = logs.getLogger(serviceName);

    initialized = true;
}

export interface OtelErrorContext {
    componentStack?: string;
    type?: string;
    [key: string]: unknown;
}

/**
 * Emit an ERROR log record. No-op until initOtel() has completed
 * (and therefore until VITE_OTEL_ENDPOINT is a parseable URL).
 */
export function logErrorToOtel(error: Error | string, context?: OtelErrorContext): void {
    if (!otelLogger) return;

    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;

    otelLogger.emit({
        severityNumber: SeverityNumber.ERROR,
        severityText: 'ERROR',
        body: message,
        attributes: {
            'exception.message': message,
            ...(stack ? { 'exception.stacktrace': stack } : {}),
            ...(context ?? {}),
        },
    });
}

export interface WebVitalForOtel {
    name: string;
    value: number;
    rating: string;
    id: string;
}

/**
 * Emit a web-vital as an INFO log record. Once SigNoz dashboards are
 * built we may switch this to a proper histogram metric, but logs give
 * the same query surface for now and avoid adding the metrics SDK to
 * the bundle.
 */
export function recordWebVitalToOtel(metric: WebVitalForOtel): void {
    if (!otelLogger) return;

    otelLogger.emit({
        severityNumber: SeverityNumber.INFO,
        severityText: 'INFO',
        body: `web_vital.${metric.name.toLowerCase()}`,
        attributes: {
            'web_vital.name': metric.name,
            'web_vital.value': metric.value,
            'web_vital.rating': metric.rating,
            'web_vital.id': metric.id,
        },
    });
}
